import { Link } from "react-router-dom";
import { useCompanies } from "@/hooks/useCompanies";
import type { Company } from "@/schemas";

/**
 * Compliance task list.
 *
 * For v1, every task is derived from existing data we already have on the
 * `companies` row — confirmation_statement / accounts dates come from
 * Companies House, CT600 is computed off the financial year-end, Self
 * Assessment is the next 31 Jan, and VAT only shows when vat_registered.
 *
 * No persistence yet — tasks are stateless and recompute on each load.
 * Marking a task complete / dismissing reminders would need a new
 * `task_completions` table; deferred until we know if anyone wants it.
 */
export default function Tasks() {
  const companies = useCompanies();

  if (companies.isLoading) return <p className="text-muted-foreground">Loading…</p>;

  const cs = companies.data ?? [];
  const allTasks = cs.flatMap((c) => tasksForCompany(c));
  const sorted = allTasks.sort((a, b) => {
    const ad = a.dueAt ? new Date(a.dueAt).getTime() : Infinity;
    const bd = b.dueAt ? new Date(b.dueAt).getTime() : Infinity;
    return ad - bd;
  });

  return (
    <section className="space-y-8">
      <header>
        <h1 className="text-3xl font-semibold">Tasks</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Filing deadlines and other recurring jobs, pulled from Companies House
          and your company details. Sorted by what's due next.
        </p>
      </header>

      {cs.length === 0 ? (
        <div className="rounded-lg border p-8 text-center text-muted-foreground">
          Add a company first.{" "}
          <Link
            to="/dashboard/app/companies/new"
            className="text-foreground underline"
          >
            Add a company →
          </Link>
        </div>
      ) : sorted.length === 0 ? (
        <div className="rounded-lg border p-8 text-center text-muted-foreground">
          No deadlines computed yet. Connect FreeAgent so we have enough data, or
          re-run the Companies House lookup on your company.
        </div>
      ) : (
        <ul className="divide-y rounded-lg border">
          {sorted.map((t) => (
            <li key={t.key} className="flex items-start justify-between gap-4 p-4">
              <div>
                <div className="font-medium">{t.title}</div>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {t.description}
                </p>
                <div className="mt-1 text-xs text-muted-foreground">
                  {t.companyName} · {t.category}
                </div>
              </div>
              <div className="shrink-0 text-right text-sm">
                {t.dueAt ? (
                  <>
                    <div className="font-medium">{formatDueDate(t.dueAt)}</div>
                    <div className="text-xs text-muted-foreground">
                      {relativeDue(t.dueAt)}
                    </div>
                  </>
                ) : (
                  <span className="text-muted-foreground">No date</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

interface Task {
  key: string;
  title: string;
  description: string;
  category: string;
  companyName: string;
  dueAt: string | null;
}

function tasksForCompany(c: Company): Task[] {
  const isLimited = c.company_type === "limited";
  const tasks: Task[] = [];

  if (isLimited && c.next_confirmation_statement_due) {
    tasks.push({
      key: `${c.id}-cs`,
      title: "Confirmation Statement",
      description:
        "Annual filing to Companies House confirming the company's details are current.",
      category: "Companies House",
      companyName: c.name,
      dueAt: c.next_confirmation_statement_due,
    });
  }

  if (isLimited && c.next_accounts_due) {
    tasks.push({
      key: `${c.id}-accounts`,
      title: "Annual Accounts",
      description: "Statutory accounts filing to Companies House for the year.",
      category: "Companies House",
      companyName: c.name,
      dueAt: c.next_accounts_due,
    });
  }

  // Corporation Tax (CT600) due 12 months after the accounting period end.
  // Use next_accounts_period_end if we have it; otherwise skip.
  if (isLimited && c.next_accounts_period_end) {
    const periodEnd = new Date(c.next_accounts_period_end);
    if (!Number.isNaN(periodEnd.getTime())) {
      const ctDue = new Date(periodEnd);
      ctDue.setUTCFullYear(ctDue.getUTCFullYear() + 1);
      tasks.push({
        key: `${c.id}-ct`,
        title: "Corporation Tax (CT600)",
        description:
          "Filed with HMRC 12 months after the accounting period end. Payment is due 9 months and 1 day after the period end.",
        category: "HMRC",
        companyName: c.name,
        dueAt: ctDue.toISOString().slice(0, 10),
      });
    }
  }

  // Self Assessment — applies to directors of limited companies AND sole traders.
  tasks.push({
    key: `${c.id}-sa`,
    title: "Self Assessment",
    description: isLimited
      ? "If you're a director taking dividends or salary, file your personal Self Assessment by 31 January."
      : "Sole trader personal tax return — file by 31 January each year.",
    category: "HMRC",
    companyName: c.name,
    dueAt: nextJan31(),
  });

  if (c.vat_registered) {
    tasks.push({
      key: `${c.id}-vat`,
      title: "VAT return",
      description:
        "Quarterly VAT return (Making Tax Digital). Filed 1 month and 7 days after the period end.",
      category: "HMRC",
      companyName: c.name,
      dueAt: nextQuarterEnd(),
    });
  }

  return tasks;
}

/** Next upcoming 31 January as ISO date. */
function nextJan31(): string {
  const now = new Date();
  const year = now.getUTCMonth() === 0 && now.getUTCDate() <= 31 ? now.getUTCFullYear() : now.getUTCFullYear() + 1;
  return `${year}-01-31`;
}

/** Naive next quarter-end (Mar/Jun/Sep/Dec end). VAT quarters depend on the
 *  company's actual VAT scheme; this is a placeholder until we track that. */
function nextQuarterEnd(): string {
  const now = new Date();
  const m = now.getUTCMonth();
  const quarterEndMonth = Math.floor(m / 3) * 3 + 2;
  const year =
    quarterEndMonth >= m
      ? now.getUTCFullYear()
      : now.getUTCFullYear() + (quarterEndMonth < 0 ? 1 : 0);
  const lastDay = new Date(Date.UTC(year, quarterEndMonth + 1, 0));
  return lastDay.toISOString().slice(0, 10);
}

function formatDueDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function relativeDue(iso: string): string {
  const d = new Date(iso).getTime();
  const days = Math.round((d - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`;
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days <= 30) return `in ${days} days`;
  const weeks = Math.round(days / 7);
  if (weeks <= 12) return `in ${weeks} week${weeks === 1 ? "" : "s"}`;
  const months = Math.round(days / 30);
  return `in ${months} month${months === 1 ? "" : "s"}`;
}
